if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        // §10.7 *The in-flight state* (ticket 17) — the label carries the state, so the arc needs
        // somewhere to revert TO. It is not always `add to cart`: §10.7 rules three resting states
        // and `pre-order` is one of them, so the resting label is read off the button rather than
        // taken from `variantStrings.addToCart`, which would silently relabel every deposit.
        this.restingButtonText = this.submitButtonText.textContent;
        this.statusElement = this.querySelector('.product-form__status');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // §10.7 — `aria-disabled` and NOT native `disabled`, and the inversion against this row's
        // sold-out ruling is deliberate: native `disabled` on a FOCUSED button removes it from the
        // accessibility tree and the tab order mid-interaction, so the browser drops focus to
        // `<body>` and the live region below announces into nowhere. The guard is the early return
        // at the top of this handler. Sold out is a resting state nobody has focused; in-flight is
        // a transient state the user is standing in.
        this.submitButton.setAttribute('aria-disabled', true);

        // §10.7 — THE WORD IS THE IN-FLIGHT STATE. `classList.add('loading')` and the two
        // `.loading__spinner` toggles were removed with the spinner itself: `.button.loading` never
        // matched this button (`product-form__submit temi-button`), so Dawn's centring rule never
        // fired and the ring span flush against the label's last letter. Leaving the toggles would
        // be a JS reference to a node that is gone.
        clearTimeout(this.revertTimer);
        this.submitButtonText.textContent = window.variantStrings.adding;
        // Cleared so a second add is a genuine text change in the live region; setting the same
        // string twice does not re-announce on every screen reader.
        this.announce('');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // §10.7 — THE SECOND SIGNAL IS THE HEADER'S `bag (n)`, RE-RENDERED FROM THE SERVER. Two
        // sources of truth for one number diverge the first time an add partially fails or a
        // quantity rule clamps it, and that IS the desync settings-prune ticket 02 ruled out — a
        // ruling true only while the add was a full navigation, which this ticket removed.
        // ⚠️ `cart.js`'s getSectionsToRender() cannot be reused: it reads `#main-cart-items`, which
        // exists only on `/cart`. The ask rides on the add request rather than costing a second
        // round trip; `data-id` is on the wrapper because the header renders inside header-group,
        // so its section id is not the bare handle.
        // ⚠️ The two branches are EXCLUSIVE, not additive: `sections` is a single form field and
        // appending it twice sends two values. Dawn's branch is unreachable in this theme — both
        // elements `:11` looks for are off disk — and is kept only as the platform's own plumbing.
        const cartCountElement = document.getElementById('temi-cart-count');
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        } else if (cartCountElement) {
          formData.append('sections', cartCountElement.dataset.id);
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              // §10.7 *The in-flight state* — NO NAVIGATION. This branch used to read
              // `window.location = window.routes.cart_url`: Dawn navigates when it finds no cart
              // element, and both of the elements `:11` looks for went off disk with the settings
              // prune, so every add on this theme displaced the buyer off the product they were
              // reading. ⚠️ THE USER RULED ADD-IN-PLACE AGAINST THE RECOMMENDATION to keep the
              // navigation (§10.7 cut accelerated checkout, so `/cart` is the only purchase path
              // and arriving there is the flow) — recorded as a reversal, not a preference.
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.renderCartCount(response);
              this.submitButtonText.textContent = window.variantStrings.added;
              this.announce(window.variantStrings.added);
              // ⚠️ 2000ms IS DECLARED, NOT DERIVED, and lands on no scale in §4 or §5 — §5 governs
              // animation, not how long a state persists. One consumer, named in §5 as a one-off.
              // Do not tokenise it. *Rejected:* reverting on the next interaction instead (a stale
              // `added` greets the buyer on their return) and holding `added` permanently (they may
              // want a second one).
              this.revertTimer = setTimeout(() => {
                this.submitButtonText.textContent = this.restingButtonText;
              }, 2000);
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            // §10.7 — the failure paths put the RESTING label back, and the success path is the one
            // exception: it has already written `added` and owns the 2000ms revert. Testing the
            // label rather than threading a flag keeps the two exits from having to agree.
            if (this.submitButtonText.textContent === window.variantStrings.adding) {
              this.submitButtonText.textContent = this.restingButtonText;
            }
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // §10.7 — the count comes from the server. Same shape as `cart.js`'s own section write-back,
      // deliberately not a call INTO it: `getSectionsToRender()` reads `#main-cart-items`, absent
      // on a product page.
      renderCartCount(response) {
        const target = document.getElementById('temi-cart-count');
        const html = response.sections?.[target?.dataset.id];
        if (!target || !html) return;

        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('#temi-cart-count');
        if (source) target.innerHTML = source.innerHTML;
      }

      announce(message) {
        if (!this.statusElement) return;
        this.statusElement.textContent = message;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
